import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/AdminSidebar';
import BilingualInput from '../../components/admin/BilingualInput';
import BilingualTextArea from '../../components/admin/BilingualTextArea';
import {
    Building2,
    Home,
    Type,
    PanelBottom,
    Mail,
    HardHat,
    DollarSign,
    Zap,
    LogOut,
    Loader2,
    CheckCircle2,
    Save,
    Layout,
    ChevronDown,
    ChevronUp,
    RefreshCw,
    Mountain,
    MapPin,
    Image as ImageIcon,
    Target,
    Plane,
    Truck,
    Info,
    Briefcase,
    Eye,
    EyeOff
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const isFooterFeatureEnabled = (value: unknown, defaultValue = true) => {
    if (value === undefined || value === null || value === '') return defaultValue;
    return ['true', '1', 'yes', 'on'].includes(String(value).trim().toLowerCase());
};

const API_BASE = '/api';
const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const AdminFooter: React.FC = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'text' | 'media'>('text');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState<string | null>(null);
    const [content, setContent] = useState<any>({});
    const [expandedSections, setExpandedSections] = useState<string[]>(['Company Info', 'Quick Links', 'Social Media']);
    const [pendingImages, setPendingImages] = useState<{ [key: string]: { file: File, preview: string } }>({});
    const username = localStorage.getItem('adminUser') || 'Admin';

    useEffect(() => {
        const token = localStorage.getItem('adminToken');
        if (!token) {
            navigate('/admin/login');
        } else {
            fetchContent();
        }
    }, [navigate]);

    const fetchContent = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE}/homepage/content?ts=${Date.now()}`, { cache: 'no-store' });
            const data = await response.json();
            data.Footer = {
                ...(data.Footer || {}),
                newsletter_enabled: data.Footer?.newsletter_enabled ?? 'true',
                staff_enabled: data.Footer?.staff_enabled ?? 'true',
                staff_title: data.Footer?.staff_title || 'Staff',
                staff_link_label: data.Footer?.staff_link_label || 'Staff Login',
                staff_link_url: data.Footer?.staff_link_url || 'https://owa.hamdanoman.com/owa/auth/logon.aspx?replaceCurrent=1&url=https%3a%2f%2fowa.hamdanoman.com%2fowa%2f'
            };
            setContent(data);
        } catch (err) {
            console.error('Failed to fetch content');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        navigate('/admin/login');
    };

    const toggleSection = (name: string) => {
        setExpandedSections(prev =>
            prev.includes(name) ? prev.filter(s => s !== name) : [...prev, name]
        );
    };

    const authedJsonPost = async (url: string, body: any) => {
        const response = await fetch(`${url}${url.includes('?') ? '&' : '?'}ts=${Date.now()}`, {
            method: 'POST',
            cache: 'no-store',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
            },
            body: JSON.stringify(body)
        });

        if (response.status === 401 || response.status === 403) {
            navigate('/admin/login');
            return false;
        }

        if (!response.ok) {
            throw new Error(`Request failed: ${response.status}`);
        }

        return true;
    };

    const authedSimplePost = async (url: string, body: any) => {
        const response = await fetch(`${url}${url.includes('?') ? '&' : '?'}ts=${Date.now()}`, {
            method: 'POST',
            cache: 'no-store',
            headers: {
                'Content-Type': 'text/plain'
            },
            body: JSON.stringify({
                ...body,
                token: localStorage.getItem('adminToken')
            })
        });

        if (response.status === 401 || response.status === 403) {
            navigate('/admin/login');
            return false;
        }

        if (!response.ok) {
            throw new Error(`Simple request failed: ${response.status}`);
        }

        return true;
    };

    const verifyContentSaved = async (sectionName: string, updates: Record<string, any>) => {
        const response = await fetch(`${API_BASE}/homepage/content?ts=${Date.now()}`, { cache: 'no-store' });
        if (!response.ok) return false;

        const latest = await response.json();
        const latestSection = latest[sectionName] || {};

        return Object.entries(updates).every(([key, value]) => {
            if (value === undefined || value === null) return true;
            return String(latestSection[key] ?? '') === String(value);
        });
    };

    const postHomepageText = async (sectionName: string, key: string, value: string, valueAr: string | null = null) => {
        return authedJsonPost(`${API_BASE}/homepage/update-text`, {
            section: sectionName,
            content_key: key,
            content_value: value,
            content_value_ar: valueAr
        });
    };

    const saveContentUpdates = async (sectionName: string, updates: Record<string, any>) => {
        const textEntries = Object.entries(updates)
            .filter(([key, value]) => !key.endsWith('_ar') && typeof value === 'string')
            .map(([key, value]) => [key, value as string] as [string, string]);

        let lastError: unknown = null;

        for (let attempt = 0; attempt < 2; attempt += 1) {
            try {
                const saved = await authedJsonPost(`${API_BASE}/content/${encodeURIComponent(sectionName)}`, updates);
                if (!saved) return false;
                return true;
            } catch (err) {
                lastError = err;
                await wait(600);
            }
        }

        try {
            const saved = await authedSimplePost(`${API_BASE}/content-simple/${encodeURIComponent(sectionName)}`, { updates });
            if (!saved) return false;
            return true;
        } catch (err) {
            lastError = err;
        }

        try {
            for (const [key, value] of textEntries) {
                const saved = await postHomepageText(sectionName, key, value, updates[`${key}_ar`] || null);
                if (!saved) return false;
            }
            return true;
        } catch (err) {
            lastError = err;
        }

        await wait(800);
        try {
            if (await verifyContentSaved(sectionName, updates)) return true;
        } catch (verifyError) {
            lastError = verifyError;
        }

        throw lastError;
    };

    const saveSectionText = async (sectionName: string, fieldKeys?: string[], savingKey = sectionName) => {
        setSaving(savingKey);
        const sectionData = content[sectionName] || {};

        try {
            const updates = (fieldKeys || Object.keys(sectionData))
                .filter(key => !key.endsWith('_ar') && typeof sectionData[key] === 'string')
                .reduce((acc: Record<string, any>, key) => {
                    acc[key] = sectionData[key];
                    acc[`${key}_ar`] = sectionData[`${key}_ar`] || null;
                    return acc;
                }, {});

            const saved = await saveContentUpdates(sectionName, updates);
            if (!saved) return;

            window.dispatchEvent(new Event('footer-settings-updated'));
        } catch (err) {
            console.error(`Save failed for ${sectionName}`, err);
        } finally {
            setTimeout(() => setSaving(null), 1000);
        }
    };

    const handleImageSelect = (section: string, key: string, file: File) => {
        const preview = URL.createObjectURL(file);
        setPendingImages(prev => ({
            ...prev,
            [`${section}-${key}`]: { file, preview }
        }));
    };

    const saveAllPendingImages = async () => {
        const totalPending = Object.keys(pendingImages).length;
        if (totalPending === 0) return;

        setSaving('all-images');
        try {
            const promises = Object.entries(pendingImages).map(async ([key, pending]) => {
                const parts = key.split('-');
                const section = parts[0];
                const keyName = parts.slice(1).join('-');

                const formData = new FormData();
                formData.append('section', section);
                formData.append('content_key', keyName);
                formData.append('image', pending.file);

                const response = await fetch('/api/homepage/update-image', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                    },
                    body: formData
                });

                if (response.ok) {
                    const data = await response.json();
                    return { section, keyName, imageUrl: data.imageUrl };
                } else {
                    const errorData = await response.json();
                    console.error(`Upload failed for ${section}-${keyName}:`, errorData);
                    return null;
                }
            });

            const results = await Promise.all(promises);

            setContent((prev: any) => {
                const next = { ...prev };
                results.forEach(res => {
                    if (res) {
                        next[res.section] = {
                            ...(next[res.section] || {}),
                            [res.keyName]: res.imageUrl
                        };
                    }
                });
                return next;
            });

            setPendingImages({});
        } catch (err) {
            console.error('Batch upload failed');
        } finally {
            setTimeout(() => setSaving(null), 1000);
        }
    };

    const setFooterToggle = async (key: 'newsletter_enabled' | 'staff_enabled', enabled: boolean) => {
        if (saving) return;
        const previousValue = content.Footer?.[key];
        const nextValue = enabled ? 'true' : 'false';

        setContent((prev: any) => ({
            ...prev,
            Footer: {
                ...(prev.Footer || {}),
                [key]: nextValue
            }
        }));

        setSaving(key);

        try {
            const saved = await saveContentUpdates('Footer', { [key]: nextValue });
            if (!saved) return;
            window.dispatchEvent(new Event('footer-settings-updated'));
        } catch (err) {
            console.error(`Failed to save ${key}`, err);
            setContent((prev: any) => ({
                ...prev,
                Footer: {
                    ...(prev.Footer || {}),
                    [key]: previousValue ?? 'true'
                }
            }));
        } finally {
            setTimeout(() => setSaving(null), 1000);
        }
    };

    const VisibilityToggle = ({ label, description, fieldKey }: { label: string; description: string; fieldKey: 'newsletter_enabled' | 'staff_enabled' }) => {
        const enabled = isFooterFeatureEnabled(content.Footer?.[fieldKey]);
        const isToggleSaving = saving === fieldKey;
        return (
            <button
                type="button"
                onClick={() => setFooterToggle(fieldKey, !enabled)}
                disabled={!!saving}
                aria-pressed={enabled}
                className={`w-full flex items-center justify-between gap-4 p-5 rounded-2xl border transition-all text-left ${
                    enabled
                        ? 'bg-emerald-50 border-emerald-100 text-emerald-700'
                        : 'bg-slate-50 border-slate-100 text-slate-500'
                } ${saving ? 'cursor-wait opacity-75' : ''}`}
            >
                <span>
                    <span className="block text-[10px] font-black uppercase tracking-widest">{label}</span>
                    <span className="block text-sm font-semibold mt-1">{description}</span>
                </span>
                <span className={`shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest transition-all ${
                    enabled ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'
                }`}>
                    {isToggleSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    {isToggleSaving ? 'Saving' : enabled ? 'On' : 'Off'}
                </span>
            </button>
        );
    };

    const UploadBox = ({ section, keyName, label, currentImage, aspect = "aspect-video" }: any) => {
        const isIndividualUploading = saving === `${section}-${keyName}`;
        const isBatchUploading = saving === 'all-images';
        const pending = pendingImages[`${section}-${keyName}`];
        const displayImage = pending ? pending.preview : currentImage;

        return (
            <div className="group bg-white p-8 rounded-3xl border border-slate-100 shadow-sm hover:border-blue-200 transition-all flex flex-col">
                <div className="flex justify-between items-center mb-6">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{label}</span>
                    {currentImage && !pending && (
                        <div className="flex items-center gap-1.5 text-emerald-500 bg-emerald-50 px-2 py-1 rounded-lg">
                            <CheckCircle2 className="w-3 h-3" />
                            <span className="text-[8px] font-black uppercase">Live</span>
                        </div>
                    )}
                    {pending && (
                        <div className="flex items-center gap-1.5 text-amber-500 bg-amber-50 px-2 py-1 rounded-lg">
                            <div className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse" />
                            <span className="text-[8px] font-black uppercase">Pending Save</span>
                        </div>
                    )}
                </div>

                <div className={`relative ${aspect} bg-slate-50 rounded-2xl overflow-hidden mb-6 border border-slate-100 flex items-center justify-center ring-offset-4 ring-blue-500/0 group-hover:ring-4 transition-all shadow-inner`}>
                    {displayImage ? (
                        <img src={displayImage} className="max-w-full max-h-full object-contain transition-transform duration-500 group-hover:scale-105 p-4" alt="Preview" />
                    ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-slate-300">
                            <ImageIcon className="w-10 h-10 mb-2 opacity-20" />
                            <span className="text-[10px] font-black uppercase tracking-widest">No Asset Loaded</span>
                        </div>
                    )}
                    <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-[1px]">
                        <label className="cursor-pointer bg-white text-blue-600 p-4 rounded-2xl font-bold hover:scale-110 transition-transform shadow-2xl flex items-center space-x-3">
                            <RefreshCw className="w-5 h-5" />
                            <input
                                type="file"
                                className="hidden"
                                accept="image/*"
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleImageSelect(section, keyName, file);
                                }}
                            />
                        </label>
                    </div>
                    {(isIndividualUploading || isBatchUploading) && (
                        <div className="absolute inset-0 bg-white/90 flex flex-col items-center justify-center">
                            <div className="w-10 h-10 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin mb-3" />
                            <span className="text-[10px] font-black text-blue-600 uppercase">Synchronizing...</span>
                        </div>
                    )}
                </div>
                {(currentImage || pending) && (
                    <div className="flex items-center justify-between mt-auto pt-4 border-t border-slate-50">
                        <span className="text-[8px] text-slate-400 font-bold uppercase tracking-tighter truncate max-w-[140px]">
                            {pending ? pending.file.name : (currentImage ? currentImage.split('/').pop() : '')}
                        </span>
                        <span className="text-[8px] text-blue-400 font-black">
                            {pending ? `${(pending.file.size / 1024).toFixed(0)} KB` : "Web Optimized"}
                        </span>
                    </div>
                )}
            </div>
        );
    };

    const SectionHeader = ({ title, icon: Icon, sectionKey, fields }: any) => (
        <div className="flex items-center justify-between p-6 bg-white rounded-3xl border border-slate-100 shadow-sm mb-4">
            <div className="flex items-center space-x-4 cursor-pointer" onClick={() => toggleSection(title)}>
                <div className={`p-3 rounded-2xl transition-colors ${expandedSections.includes(title) ? 'bg-blue-600 text-white shadow-lg shadow-blue-100' : 'bg-blue-50 text-blue-600'}`}>
                    <Icon className="w-6 h-6" />
                </div>
                <div>
                    <h2 className="text-xl font-black text-slate-800 tracking-tight">{title}</h2>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">Configuration Panel</p>
                </div>
                {expandedSections.includes(title) ? <ChevronUp className="w-5 h-5 text-slate-300" /> : <ChevronDown className="w-5 h-5 text-slate-300" />}
            </div>
            {sectionKey && (
                <button
                    onClick={() => saveSectionText(sectionKey, fields, title)}
                    disabled={saving === title}
                    className="flex items-center space-x-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-200 text-white rounded-2xl font-black text-xs uppercase tracking-widest transition-all shadow-xl shadow-emerald-100 active:scale-95"
                >
                    {saving === title ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    <span>Sync Content</span>
                </button>
            )}
        </div>
    );

    return (
        <div className="flex h-screen bg-slate-50 font-sans overflow-hidden">
            <AdminSidebar />

            {/* Main Area */}
            <div className="flex-1 flex flex-col h-full bg-slate-50/50">
                {/* Fixed Header */}
                <header className="h-20 bg-white border-b border-slate-200 flex items-center justify-between px-10 shadow-sm shrink-0 z-30">
                    <div className="flex items-center space-x-6">
                        <h2 className="text-xl font-extrabold text-slate-800 flex items-center gap-2">
                            <PanelBottom className="w-6 h-6 text-blue-600" /> Footer Editor
                        </h2>

                        <div className="flex bg-slate-100 p-1.5 rounded-2xl">
                            <button
                                onClick={() => setActiveTab('text')}
                                className={`flex items-center space-x-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${activeTab === 'text' ? 'bg-white text-blue-600 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                <Type className="w-4 h-4" />
                                <span>Content Details</span>
                            </button>
                            <button
                                onClick={() => setActiveTab('media')}
                                className={`flex items-center space-x-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${activeTab === 'media' ? 'bg-white text-blue-600 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                <ImageIcon className="w-4 h-4" />
                                <span>Media Assets</span>
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center space-x-6">
                        <div className="hidden sm:block text-right">
                            <div className="text-sm font-bold text-slate-900">{username}</div>
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Portal Manager</div>
                        </div>
                        <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center border border-slate-200 shadow-inner">
                            <CheckCircle2 className="w-5 h-5 text-blue-600" />
                        </div>
                    </div>
                </header>

                <main className="flex-1 overflow-y-auto p-10 custom-scrollbar scroll-smooth">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-40">
                            <div className="w-16 h-16 border-4 border-blue-50 border-t-blue-600 rounded-full animate-spin mb-6" />
                            <p className="text-slate-400 font-black uppercase tracking-widest text-[10px]">Syncing Architecture...</p>
                        </div>
                    ) : (
                        <div className="max-w-6xl mx-auto">
                            <AnimatePresence mode="wait">
                                {activeTab === 'text' ? (
                                    <motion.div
                                        key="text-tab"
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        className="space-y-6"
                                    >
                                        <SectionHeader
                                            title="Company Info"
                                            icon={Building2}
                                            sectionKey="Footer"
                                            fields={['description', 'address', 'phone', 'email']}
                                        />
                                        <AnimatePresence>
                                            {expandedSections.includes('Company Info') && (
                                                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                                    <div className="bg-white p-10 rounded-[32px] border border-slate-100 shadow-sm space-y-8 ml-4 mb-4">
                                                        <BilingualTextArea 
                                                            label="Company Bio"
                                                            englishValue={content.Footer?.description || ''}
                                                            arabicValue={content.Footer?.description_ar}
                                                            onChangeEnglish={val => setContent({ ...content, Footer: { ...(content.Footer || {}), description: val } })}
                                                            onChangeArabic={val => setContent({ ...content, Footer: { ...(content.Footer || {}), description_ar: val } })}
                                                            rows={6}
                                                        />
                                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                                            <BilingualInput 
                                                                label="Global HQ Address"
                                                                englishValue={content.Footer?.address || ''}
                                                                arabicValue={content.Footer?.address_ar}
                                                                onChangeEnglish={val => setContent({ ...content, Footer: { ...(content.Footer || {}), address: val } })}
                                                                onChangeArabic={val => setContent({ ...content, Footer: { ...(content.Footer || {}), address_ar: val } })}
                                                            />
                                                            <BilingualInput 
                                                                label="Direct Contact Line"
                                                                englishValue={content.Footer?.phone || ''}
                                                                arabicValue={content.Footer?.phone_ar}
                                                                onChangeEnglish={val => setContent({ ...content, Footer: { ...(content.Footer || {}), phone: val } })}
                                                                onChangeArabic={val => setContent({ ...content, Footer: { ...(content.Footer || {}), phone_ar: val } })}
                                                            />
                                                            <BilingualInput 
                                                                label="Corporate Email"
                                                                englishValue={content.Footer?.email || ''}
                                                                arabicValue={content.Footer?.email_ar}
                                                                onChangeEnglish={val => setContent({ ...content, Footer: { ...(content.Footer || {}), email: val } })}
                                                                onChangeArabic={val => setContent({ ...content, Footer: { ...(content.Footer || {}), email_ar: val } })}
                                                            />
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>

                                        <SectionHeader
                                            title="Quick Links"
                                            icon={Layout}
                                            sectionKey="Footer"
                                            fields={['quick_link_1', 'quick_link_2', 'quick_link_3', 'quick_link_4']}
                                        />
                                        <AnimatePresence>
                                            {expandedSections.includes('Quick Links') && (
                                                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                                    <div className="bg-white p-10 rounded-[32px] border border-slate-100 shadow-sm grid grid-cols-1 md:grid-cols-2 gap-8 ml-4 mb-4">
                                                        {[1, 2, 3, 4].map(i => (
                                                            <BilingualInput 
                                                                key={i}
                                                                label={`Link Slot ${i}`}
                                                                englishValue={content.Footer?.[`quick_link_${i}`] || ''}
                                                                arabicValue={content.Footer?.[`quick_link_${i}_ar`]}
                                                                onChangeEnglish={val => setContent({ ...content, Footer: { ...(content.Footer || {}), [`quick_link_${i}`]: val } })}
                                                                onChangeArabic={val => setContent({ ...content, Footer: { ...(content.Footer || {}), [`quick_link_${i}_ar`]: val } })}
                                                            />
                                                        ))}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>

                                        <SectionHeader
                                            title="Social Media"
                                            icon={Zap}
                                            sectionKey="Footer"
                                            fields={['social_facebook', 'social_twitter', 'social_instagram', 'social_linkedin']}
                                        />
                                        <AnimatePresence>
                                            {expandedSections.includes('Social Media') && (
                                                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                                    <div className="bg-white p-10 rounded-[32px] border border-slate-100 shadow-sm grid grid-cols-1 md:grid-cols-2 gap-6 ml-4 mb-4">
                                                        {['facebook', 'twitter', 'instagram', 'linkedin'].map(network => (
                                                            <div key={network} className="space-y-2">
                                                                <label className="text-[10px] font-bold text-slate-400 tracking-wider uppercase">{network} URL</label>
                                                                <input
                                                                    className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none text-sm font-bold transition-all"
                                                                    placeholder={`https://${network}.com/brand`}
                                                                    value={content.Footer?.[`social_${network}`] || ''}
                                                                    onChange={(e) => setContent({ ...content, Footer: { ...(content.Footer || {}), [`social_${network}`]: e.target.value } })}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>

                                        <SectionHeader
                                            title="Newsletter"
                                            icon={Mail}
                                            sectionKey="Footer"
                                            fields={['newsletter_enabled', 'newsletter_desc']}
                                        />
                                        <AnimatePresence>
                                            {expandedSections.includes('Newsletter') && (
                                                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                                    <div className="bg-white p-10 rounded-[32px] border border-slate-100 shadow-sm ml-4 mb-4 space-y-8">
                                                        <VisibilityToggle
                                                            label="Newsletter Footer"
                                                            description="Show newsletter subscription block"
                                                            fieldKey="newsletter_enabled"
                                                        />
                                                        <BilingualInput 
                                                            label="Subscription Text"
                                                            englishValue={content.Footer?.newsletter_desc || ''}
                                                            arabicValue={content.Footer?.newsletter_desc_ar}
                                                            onChangeEnglish={val => setContent({ ...content, Footer: { ...(content.Footer || {}), newsletter_desc: val } })}
                                                            onChangeArabic={val => setContent({ ...content, Footer: { ...(content.Footer || {}), newsletter_desc_ar: val } })}
                                                        />
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>

                                        <SectionHeader
                                            title="Staff"
                                            icon={Briefcase}
                                            sectionKey="Footer"
                                            fields={['staff_enabled', 'staff_title', 'staff_link_label', 'staff_link_url']}
                                        />
                                        <AnimatePresence>
                                            {expandedSections.includes('Staff') && (
                                                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                                    <div className="bg-white p-10 rounded-[32px] border border-slate-100 shadow-sm ml-4 mb-4 space-y-8">
                                                        <VisibilityToggle
                                                            label="Staff Footer"
                                                            description="Show staff portal link"
                                                            fieldKey="staff_enabled"
                                                        />
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                                            <BilingualInput
                                                                label="Footer Header"
                                                                englishValue={content.Footer?.staff_title || ''}
                                                                arabicValue={content.Footer?.staff_title_ar}
                                                                onChangeEnglish={val => setContent({ ...content, Footer: { ...(content.Footer || {}), staff_title: val } })}
                                                                onChangeArabic={val => setContent({ ...content, Footer: { ...(content.Footer || {}), staff_title_ar: val } })}
                                                            />
                                                            <BilingualInput
                                                                label="Link Text"
                                                                englishValue={content.Footer?.staff_link_label || ''}
                                                                arabicValue={content.Footer?.staff_link_label_ar}
                                                                onChangeEnglish={val => setContent({ ...content, Footer: { ...(content.Footer || {}), staff_link_label: val } })}
                                                                onChangeArabic={val => setContent({ ...content, Footer: { ...(content.Footer || {}), staff_link_label_ar: val } })}
                                                            />
                                                        </div>
                                                        <div className="space-y-2">
                                                            <label className="text-[10px] font-bold text-slate-400 tracking-wider uppercase">Staff Portal URL</label>
                                                            <input
                                                                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none text-sm font-bold transition-all"
                                                                placeholder="https://owa.hamdanoman.com/owa/"
                                                                value={content.Footer?.staff_link_url || ''}
                                                                onChange={(e) => setContent({ ...content, Footer: { ...(content.Footer || {}), staff_link_url: e.target.value } })}
                                                            />
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="media-tab"
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        className="space-y-12"
                                    >
                                        <div className="flex items-center justify-between border-b border-slate-100 pb-8">
                                            <div>
                                                <h3 className="text-3xl font-black text-slate-800 tracking-tight">Media Library</h3>
                                                <p className="text-slate-500 text-sm font-medium">Manage global footer assets and logos</p>
                                            </div>
                                            <button
                                                onClick={saveAllPendingImages}
                                                disabled={Object.keys(pendingImages).length === 0 || !!saving}
                                                className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all shadow-2xl shadow-blue-100 active:scale-95 disabled:bg-slate-200"
                                            >
                                                {saving === 'all-images' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 inline mr-2" />}
                                                Synchronize Assets
                                            </button>
                                        </div>

                                        <div className="max-w-xl">
                                            <UploadBox
                                                section="Footer"
                                                keyName="logo_image"
                                                label="Footer Brand Identity"
                                                currentImage={content.Footer?.logo_image}
                                                aspect="aspect-[3/1]"
                                            />
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    )}
                </main>
            </div>


        </div>
    );
};

export default AdminFooter;
